import { cn } from "@/lib/utils";
import { KdsTimer } from "./KdsTimer";
import { Play, Check, RotateCcw, CheckCheck, X, AlertTriangle } from "lucide-react";
import type { KdsTicket, KdsTicketItem, KdsItemStatus } from "@/types/kds";

interface KdsTicketCardProps {
  ticket: KdsTicket;
  slaWarning?: number;
  slaCritical?: number;
  onStartItem: (item: KdsTicketItem) => void;
  onDoneItem: (item: KdsTicketItem) => void;
  onRecallItem: (item: KdsTicketItem) => void;
  onDoneTicket: (items: KdsTicketItem[]) => void;
  onRecallTicket: (items: KdsTicketItem[]) => void;
}

function getOrderTypeLabel(type: string): string {
  switch (type) {
    case "table": return "Mesa";
    case "tab": return "Comanda";
    case "takeout": return "Retirada";
    case "delivery": return "Delivery";
    default: return type;
  }
}

function getOrderIdentifier(ticket: KdsTicket): string {
  if (ticket.tableNumber) return `Mesa ${ticket.tableNumber}`;
  if (ticket.tabNumber) return `Comanda ${ticket.tabNumber}`;
  if (ticket.customerName) return ticket.customerName;
  return `#${ticket.orderNumber}`;
}

const statusBorderColor: Record<KdsItemStatus, string> = {
  new: "border-l-amber-400",
  in_progress: "border-l-blue-400",
  done: "border-l-emerald-400",
  recalled: "border-l-orange-400",
  cancelled: "border-l-red-500",
};

export function KdsTicketCard({
  ticket,
  slaWarning = 600,
  slaCritical = 1200,
  onStartItem,
  onDoneItem,
  onRecallItem,
  onDoneTicket,
  onRecallTicket,
}: KdsTicketCardProps) {
  const allDone = ticket.items.every(i => i.status === "done");
  const hasPending = ticket.items.some(i => i.status === "new" || i.status === "in_progress" || i.status === "recalled");

  return (
    <div
      className={cn(
        "rounded-xl border-l-4 bg-zinc-800 shadow-lg flex flex-col overflow-hidden",
        statusBorderColor[ticket.status]
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-800/80 border-b border-zinc-700">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-white">#{ticket.orderNumber}</span>
          <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-300 font-medium uppercase">
            {getOrderTypeLabel(ticket.orderType)}
          </span>
        </div>
        <KdsTimer
          openedAt={ticket.oldestOpenedAt}
          slaWarningSeconds={slaWarning}
          slaCriticalSeconds={slaCritical}
        />
      </div>

      {/* Identifier */}
      <div className="px-4 py-2 border-b border-zinc-700/50">
        <span className="text-sm text-zinc-400">{getOrderIdentifier(ticket)}</span>
        {ticket.waiterName && (
          <span className="text-xs text-zinc-500 ml-2">• {ticket.waiterName}</span>
        )}
      </div>

      {/* Items */}
      <div className="flex-1 px-4 py-2 space-y-1">
        {ticket.items.map((item) => (
          <div key={item.id} className={cn(
            "flex items-start gap-2 py-1.5",
            item.status === "cancelled" && "bg-red-500/10 rounded px-1 -mx-1"
          )}>
            {/* Status action button - hidden for cancelled */}
            {item.status === "cancelled" ? (
              <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded flex items-center justify-center bg-red-500/20 text-red-400">
                <X className="w-3.5 h-3.5" />
              </div>
            ) : (
              <button
                onClick={() => {
                  if (item.status === "new" || item.status === "recalled") onStartItem(item);
                  else if (item.status === "in_progress") onDoneItem(item);
                  else if (item.status === "done") onRecallItem(item);
                }}
                className={cn(
                  "mt-0.5 flex-shrink-0 w-6 h-6 rounded flex items-center justify-center transition-colors",
                  item.status === "new" && "bg-amber-500/20 text-amber-400 hover:bg-amber-500/40",
                  item.status === "in_progress" && "bg-blue-500/20 text-blue-400 hover:bg-blue-500/40",
                  item.status === "done" && "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/40",
                  item.status === "recalled" && "bg-orange-500/20 text-orange-400 hover:bg-orange-500/40"
                )}
              >
                {item.status === "new" && <Play className="w-3.5 h-3.5" />}
                {item.status === "recalled" && <Play className="w-3.5 h-3.5" />}
                {item.status === "in_progress" && <Check className="w-3.5 h-3.5" />}
                {item.status === "done" && <RotateCcw className="w-3.5 h-3.5" />}
              </button>
            )}

            <div className="flex-1 min-w-0">
              <div className={cn(
                "flex items-baseline gap-2",
                (item.status === "done" || item.status === "cancelled") && "opacity-50 line-through"
              )}>
                <span className={cn("text-base font-bold", item.status === "cancelled" ? "text-red-400" : "text-white")}>{item.quantity}x</span>
                <span className={cn("text-base truncate", item.status === "cancelled" ? "text-red-400" : "text-white")}>{item.name_snapshot}</span>
              </div>
              {/* Alert if item was in progress when cancelled */}
              {item.status === "cancelled" && item.started_at && (
                <div className="flex items-center gap-1 mt-1 px-2 py-0.5 rounded bg-red-500/20 w-fit">
                  <AlertTriangle className="w-3 h-3 text-red-400" />
                  <span className="text-xs font-bold text-red-400 uppercase">EM PREPARO - CANCELADO!</span>
                </div>
              )}
              {item.notes_snapshot && (
                <p className={cn(
                  "text-sm mt-0.5 leading-tight",
                  item.status === "cancelled" ? "text-red-400" : "text-amber-400"
                )}>
                  {item.status === "cancelled" ? "🚫" : "⚠"} {item.notes_snapshot}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer actions */}
      <div className="px-4 py-2 border-t border-zinc-700 flex gap-2">
        {hasPending ? (
          <button
            onClick={() => onDoneTicket(ticket.items)}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors text-sm"
          >
            <CheckCheck className="w-4 h-4" />
            Finalizar Tudo
          </button>
        ) : allDone ? (
          <button
            onClick={() => onRecallTicket(ticket.items)}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-zinc-300 font-medium transition-colors text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Recall
          </button>
        ) : null}
      </div>
    </div>
  );
}
